import { useQueryClient } from '@tanstack/react-query';
import { useApi } from './useApi';
import { useModeAwareMutation, useModeAwareQuery } from './useModeAwareQuery';

// ── API response / request shapes ────────────────────────────────────────────

export interface RiskCreditSettingsResponse {
  id?: number;
  organisationId?: number;
  notificationEnabled: boolean;
  notifyCreditController: boolean;
  notifyUserIds: number[];
  warningThreshold: number;
}

export interface UpsertRiskCreditSettingsRequest {
  notificationEnabled: boolean;
  notifyCreditController: boolean;
  notifyUserIds: number[];
  warningThreshold: number;
}

// ── Demo / mock data ─────────────────────────────────────────────────────────

const MOCK_RISK_CREDIT_SETTINGS: RiskCreditSettingsResponse = {
  notificationEnabled: false,
  notifyCreditController: false,
  notifyUserIds: [],
  warningThreshold: 0.8,
};

// ── Query ────────────────────────────────────────────────────────────────────

/**
 * Hook to fetch risk credit settings.
 * Handles both Demo and Live modes.
 */
export function useRiskCreditSettings() {
  const { apiGet } = useApi();

  return useModeAwareQuery<RiskCreditSettingsResponse, Error>(
    ['risk-credit-settings'],
    async ({ signal }) => {
      return apiGet<RiskCreditSettingsResponse>('/api/v1/settings/risk-credit', undefined, signal);
    },
    MOCK_RISK_CREDIT_SETTINGS,
    {
      staleTime: 5 * 60 * 1000,
    },
  );
}

// ── Mutation ─────────────────────────────────────────────────────────────────

/**
 * Hook to upsert risk credit settings.
 * Handles both Demo and Live modes.
 */
export function useUpsertRiskCreditSettings() {
  const { apiPut } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<RiskCreditSettingsResponse, Error, UpsertRiskCreditSettingsRequest>(
    async (data) => {
      return apiPut<RiskCreditSettingsResponse>('/api/v1/settings/risk-credit', data);
    },
    async (data) => {
      return {
        notificationEnabled: data.notificationEnabled,
        notifyCreditController: data.notifyCreditController,
        notifyUserIds: data.notifyUserIds,
        warningThreshold: data.warningThreshold,
      };
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['risk-credit-settings'] });
      },
    },
  );
}
